import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  EMPTY_STRING,
} from '../../../constants/common'
import { REG_EXP_EMAIL } from '../../../constants/regExp'
import { getRecipientRealId } from '../../../utils/email'
import { getUserAvatar, isExternalContactUser } from '../../../utils/user'
import { toUserSelectItem } from '../../User'
import { EMAIL_SYMBOL_MAX_COUNT } from '../Email.constants'
import {
  buildEmailRecipientOptionId,
  normalizeEmail,
  parseEmailsFromInput,
} from '../Email.utils'
import {
  EMAIL_CREATION_FORM_RECIPIENT_FIELD_EMAIL_TYPE,
  isAdditionalEmail,
  isPartnerEmail,
} from '../EmailCreationForm'

import PartnerEmailIcon from '../../../icons/add-to-contacts.svg'
import EmailAdditionalIcon from '../../../icons/email-additional.svg'
import ProviderIcon from '../../../icons/provider.svg'

function makeRecipientEmailOption(sourceOption, emailEntry) {
  const optionId = buildEmailRecipientOptionId({
    email: emailEntry.email,
    id: getRecipientRealId(sourceOption),
  })

  return {
    ...sourceOption,
    additionalData: emailEntry.email,
    email: emailEntry.email,
    emailType: emailEntry.type,
    optionId,
    type: emailEntry.type,
    value: optionId,
  }
}

// Picks every entry from item.emails that isn't the user's main address. The
// main type is role-driven: external contacts use `email` as their primary,
// everyone else uses `auth-email`. A regular user can carry both an `email`
// and a `partner-email` alongside the main `auth-email`, so we keep anything
// whose type differs from the main one rather than matching a single
// "additional" type.
// The bulk recipients endpoint returns entries shaped `{type, value}`, but the
// downstream helper (expandRecipientEmailsForTo / makeRecipientEmailOption)
// reads `emailEntry.email`. Normalize to `email` so the shared helper works.
function getAdditionalContactEmails(item) {
  if (!Array.isArray(item?.emails)) {
    return EMPTY_LIST
  }

  const primaryEmailType = isExternalContactUser(item)
    ? EMAIL_CREATION_FORM_RECIPIENT_FIELD_EMAIL_TYPE.additional
    : EMAIL_CREATION_FORM_RECIPIENT_FIELD_EMAIL_TYPE.main

  return item.emails.reduce((acc, emailEntry) => {
    if (!emailEntry?.type || emailEntry.type === primaryEmailType) {
      return acc
    }

    const email = emailEntry.email ?? emailEntry.value

    if (!email) {
      return acc
    }

    return [...acc, { email, type: emailEntry.type }]
  }, [])
}

export function getRecipientItemId(
  item,
  { fallbackToEmail = false } = EMPTY_OBJECT
) {
  return (
    item?.optionId ||
    item?.value ||
    (fallbackToEmail ? normalizeEmail(item?.email) : undefined)
  )
}

export function isValidEmail(value) {
  return (
    value && value.length < EMAIL_SYMBOL_MAX_COUNT && REG_EXP_EMAIL.test(value)
  )
}

export function isEmailRecipientLocked(item) {
  return item.isLocked
}

export function getInputRecipientValue(value) {
  const validEmail = isValidEmail(value) ? value : EMPTY_STRING

  return {
    email: value,
    label: value,
    optionId: validEmail ? normalizeEmail(validEmail) : EMPTY_STRING,
    value: validEmail,
  }
}

export function addInputValue(value, ...inputValues) {
  const result = [...value.filter(v => !!v.value)]

  inputValues.forEach(input => {
    const normalized = normalizeEmail(input)
    const alreadyExists = result.some(
      item => normalizeEmail(item.email) === normalized
    )

    if (!alreadyExists) {
      result.push(getInputRecipientValue(input))
    }
  })

  return result
}

export function splitValidAndInvalidEmails(input) {
  const emails = parseEmailsFromInput(input)

  const valid = []
  const invalid = []

  emails.forEach(email => {
    if (isValidEmail(email)) {
      valid.push(email)
    } else {
      invalid.push(email)
    }
  })

  return { invalid, valid }
}

export function getRecipientsFieldApiMeta(response) {
  return {
    page: response.data.meta.curr_page,
    page_count: response.data.meta.page_count,
    total: response.data.meta.count,
  }
}

export function expandRecipientEmailsForTo(currentTo, sourceOption) {
  const realId = getRecipientRealId(sourceOption)

  if (realId === null || !Array.isArray(sourceOption?.emails)) {
    return EMPTY_LIST
  }

  const existingIds = new Set(currentTo.map(getRecipientItemId))

  return sourceOption.emails.reduce((acc, emailEntry) => {
    const email = emailEntry?.email ?? emailEntry?.value

    if (!email) {
      return acc
    }

    const candidate = makeRecipientEmailOption(sourceOption, {
      email,
      type: emailEntry.type,
    })

    if (!existingIds.has(candidate.optionId)) {
      acc.push(candidate)
      existingIds.add(candidate.optionId)
    }

    return acc
  }, [])
}

export function toEmailRecipientFieldSelectItemsFromOption(items) {
  return items.map(userData => {
    const baseItem = toUserSelectItem(userData)
    const optionId = buildEmailRecipientOptionId(userData)

    return {
      ...baseItem,
      additionalData: userData.email,
      avatar: {
        ...getUserAvatar(userData),
        Icon: userData.is_contractor ? ProviderIcon : null,
      },
      emails: userData.emails,
      emailType: userData.type,
      isContractor: userData.is_contractor,
      optionId,
      realId: userData.id,
      value: optionId,
    }
  })
}

// Resolve a recipient avatar's icon from serializable item data so it survives
// a draft round-trip through JSON (which destroys the `Icon` component). Add a
// case here as new recipient avatar icon types appear.
export function getEmailRecipientAvatarIcon(item) {
  switch (true) {
    case item?.isContractor:
      return ProviderIcon
    default:
      return null
  }
}

export function getEmailRecipientFieldSelectItemIcon(option) {
  if (isAdditionalEmail(option)) {
    return EmailAdditionalIcon
  }

  if (isPartnerEmail(option)) {
    return PartnerEmailIcon
  }

  return null
}

export function isEmailRecipientAdditional(user) {
  if (isExternalContactUser(user) || isPartnerEmail(user)) {
    return isPartnerEmail(user)
  }

  return isAdditionalEmail(user)
}

// Append a recipient per additional email from each base recipient's `emails`
// array. expandRecipientEmailsForTo dedupes by optionId against its first arg,
// so passing `[...baseRecipients, ...acc]` prevents collisions both with main
// recipients and with additions added earlier in this loop (e.g. the same
// shared email under two contacts).
export function withAdditionalContactRecipients(baseRecipients) {
  const additionalRecipients = baseRecipients.reduce((acc, baseItem) => {
    const additionalEmails = getAdditionalContactEmails(baseItem)

    if (additionalEmails.length === 0) {
      return acc
    }

    const additions = expandRecipientEmailsForTo([...baseRecipients, ...acc], {
      ...baseItem,
      emails: additionalEmails,
    })

    return [...acc, ...additions]
  }, [])

  return additionalRecipients.length > 0
    ? [...baseRecipients, ...additionalRecipients]
    : baseRecipients
}

export function getEmailRecipientsFieldValueWithLocked({
  lockedValues,
  value,
}) {
  return lockedValues.length > 0
    ? [...lockedValues.map(item => ({ ...item, isLocked: true })), ...value]
    : value
}

export function getEmailRecipientsFieldUnlockedValue({ lockedValues, value }) {
  return lockedValues.length > 0
    ? value.filter(item => !isEmailRecipientLocked(item))
    : value
}
